/**
 * The colour math behind the appearance: OKLCH to pick, WCAG luminance to
 * judge, HSL triplets out (the app's tokens are read as `hsl(var(--x))`).
 *
 * A picked colour is placed in each mode's legal band of OKLab lightness,
 * then clamped until the chrome stays readable at the current grain:
 * 1. every sidebar ink, text and icon, is at least 4.5:1 against each
 *    surface it is drawn on: the tint under the worst grain pixel, and that
 *    with each hover and fill the sidebar lays over it;
 * 2. the selected card is lighter than the tint by at least 1.1:1;
 * 3. the focus ring is at least 3:1 against the tint under the worst pixel.
 *    While a colour is picked the sidebar draws its rings at full `--ring`
 *    (index.css), so this is the ring on screen.
 */
import type { AppearanceColor, AppearanceLook } from './appearance.util';

/** A gamma-encoded sRGB colour, each channel 0 to 1. */
export interface Rgb {
  r: number;
  g: number;
  b: number;
}

export interface Oklab {
  L: number;
  a: number;
  b: number;
}

/** OKLCH with the hue in degrees, 0 to 360. */
export interface Oklch {
  L: number;
  C: number;
  h: number;
}

export type ChromeMode = 'light' | 'dark';

export const CHROME_MODES: readonly ChromeMode[] = ['light', 'dark'];

const clamp01 = (value: number) => Math.min(1, Math.max(0, value));

// Conversions --------------------------------------------------------------

export function hexToRgb(hex: string): Rgb {
  const value = hex.replace('#', '');
  const full =
    value.length === 3
      ? value
          .split('')
          .map((digit) => digit + digit)
          .join('')
      : value;
  const number = parseInt(full, 16);
  return {
    r: ((number >> 16) & 255) / 255,
    g: ((number >> 8) & 255) / 255,
    b: (number & 255) / 255,
  };
}

/** The nearest 8-bit colour, as the screen shows it. */
export function quantize(rgb: Rgb): Rgb {
  const channel = (value: number) => Math.round(clamp01(value) * 255) / 255;
  return { r: channel(rgb.r), g: channel(rgb.g), b: channel(rgb.b) };
}

export function rgbToHex(rgb: Rgb): string {
  const channel = (value: number) =>
    Math.round(clamp01(value) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${channel(rgb.r)}${channel(rgb.g)}${channel(rgb.b)}`.toUpperCase();
}

const toLinear = (channel: number) =>
  channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;

const toGamma = (channel: number) =>
  channel <= 0.0031308
    ? 12.92 * channel
    : 1.055 * Math.sign(channel) * Math.abs(channel) ** (1 / 2.4) - 0.055;

function linearToOklab(r: number, g: number, b: number): Oklab {
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  return {
    L: 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    a: 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    b: 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  };
}

/** Linear sRGB, which may fall outside 0 to 1 for a colour out of gamut. */
function oklabToLinear({ L, a, b }: Oklab): [number, number, number] {
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
}

export function rgbToOklab(rgb: Rgb): Oklab {
  return linearToOklab(toLinear(rgb.r), toLinear(rgb.g), toLinear(rgb.b));
}

/** The sRGB colour, clipped into gamut. */
export function oklabToRgb(lab: Oklab): Rgb {
  const [r, g, b] = oklabToLinear(lab);
  return {
    r: clamp01(toGamma(clamp01(r))),
    g: clamp01(toGamma(clamp01(g))),
    b: clamp01(toGamma(clamp01(b))),
  };
}

export function oklabToOklch({ L, a, b }: Oklab): Oklch {
  const C = Math.hypot(a, b);
  const h = (Math.atan2(b, a) * 180) / Math.PI;
  return { L, C, h: h < 0 ? h + 360 : h };
}

export function oklchToOklab({ L, C, h }: Oklch): Oklab {
  const radians = (h * Math.PI) / 180;
  return { L, a: C * Math.cos(radians), b: C * Math.sin(radians) };
}

export const rgbToOklch = (rgb: Rgb): Oklch => oklabToOklch(rgbToOklab(rgb));
export const oklchToRgb = (lch: Oklch): Rgb => oklabToRgb(oklchToOklab(lch));

const GAMUT_EPSILON = 1e-7;

function inGamut(lch: Oklch): boolean {
  return oklabToLinear(oklchToOklab(lch)).every(
    (channel) => channel >= -GAMUT_EPSILON && channel <= 1 + GAMUT_EPSILON
  );
}

/** The widest chroma the screen shows at this lightness and hue. */
export function maxChroma(L: number, h: number): number {
  if (L <= 0 || L >= 1) return 0;
  let inside = 0;
  let outside = 0.4;
  for (let step = 0; step < 22; step++) {
    const C = (inside + outside) / 2;
    if (inGamut({ L, C, h })) inside = C;
    else outside = C;
  }
  return inside;
}

export interface Hsl {
  /** 0 to 360. */
  h: number;
  /** 0 to 100. */
  s: number;
  /** 0 to 100. */
  l: number;
}

export function rgbToHsl({ r, g, b }: Rgb): Hsl {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const delta = max - min;
  if (delta === 0) return { h: 0, s: 0, l: l * 100 };
  const s = delta / (1 - Math.abs(2 * l - 1));
  let h: number;
  if (max === r) h = ((g - b) / delta) % 6;
  else if (max === g) h = (b - r) / delta + 2;
  else h = (r - g) / delta + 4;
  h *= 60;
  if (h < 0) h += 360;
  return { h, s: s * 100, l: l * 100 };
}

export function hslToRgb({ h, s, l }: Hsl): Rgb {
  const saturation = s / 100;
  const lightness = l / 100;
  const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
  const hue = (((h % 360) + 360) % 360) / 60;
  const x = chroma * (1 - Math.abs((hue % 2) - 1));
  const [r, g, b] =
    hue < 1
      ? [chroma, x, 0]
      : hue < 2
        ? [x, chroma, 0]
        : hue < 3
          ? [0, chroma, x]
          : hue < 4
            ? [0, x, chroma]
            : hue < 5
              ? [x, 0, chroma]
              : [chroma, 0, x];
  const offset = lightness - chroma / 2;
  return {
    r: clamp01(r + offset),
    g: clamp01(g + offset),
    b: clamp01(b + offset),
  };
}

const round2 = (value: number) => Math.round(value * 100) / 100;

/** A token's value, as `index.css` writes them: `220 35% 93%`. */
export function toHslTriplet(rgb: Rgb): string {
  const { h, s, l } = rgbToHsl(quantize(rgb));
  return `${round2(h)} ${round2(s)}% ${round2(l)}%`;
}

export function parseHslTriplet(triplet: string): Rgb {
  const [h, s, l] = triplet
    .trim()
    .split(/\s+/)
    .map((part) => parseFloat(part));
  return hslToRgb({ h, s, l });
}

// Contrast -----------------------------------------------------------------

/** WCAG relative luminance. */
export function luminance(rgb: Rgb): number {
  return (
    0.2126 * toLinear(rgb.r) +
    0.7152 * toLinear(rgb.g) +
    0.0722 * toLinear(rgb.b)
  );
}

function ratio(a: number, b: number): number {
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}

/** WCAG contrast ratio, 1 to 21. */
export function contrastRatio(a: Rgb, b: Rgb): number {
  return ratio(luminance(a), luminance(b));
}

// The chrome's rules ---------------------------------------------------------

/** The grain's opacity at 100. */
const MAX_GRAIN_ALPHA = 0.05;

export function grainAlpha(grain: number): number {
  return (MAX_GRAIN_ALPHA * Math.min(100, Math.max(0, grain))) / 100;
}

/** `top` laid over `base` at `alpha`, as the browser blends it. */
function over(base: Rgb, top: Rgb, alpha: number): Rgb {
  return {
    r: base.r * (1 - alpha) + top.r * alpha,
    g: base.g * (1 - alpha) + top.g * alpha,
    b: base.b * (1 - alpha) + top.b * alpha,
  };
}

const BLACK: Rgb = { r: 0, g: 0, b: 0 };
const WHITE: Rgb = { r: 1, g: 1, b: 1 };
const MID_GREY: Rgb = { r: 0.5, g: 0.5, b: 0.5 };

export const INK_CONTRAST = 4.5;
export const CARD_CONTRAST = 1.1;
export const RING_CONTRAST = 3;

/**
 * The tokens the chrome sets for itself while a colour is picked, as
 * index.css writes them under `html[data-appearance]`: stock muted text is
 * 4.06:1 on today's light chrome and stock tertiary text is under 4.5:1 on
 * any tint in either mode, so no tint could hold them.
 */
export const CHROME_RETUNES = {
  light: {
    '--muted-foreground': '215 14% 34%',
    '--tertiary-foreground': '0 0% 35%',
  },
  dark: { '--tertiary-foreground': '0 0% 54%' },
} as const satisfies Record<ChromeMode, Record<string, string>>;

export type ChromeInkName =
  | 'foreground'
  | 'label'
  | 'icon'
  | 'muted'
  | 'tertiary'
  | 'link';

/**
 * The sidebar's inks in each mode: the foreground, the tile labels (gray-700
 * or gray-300), the icons and their labels (SIDEBAR_ICON, gray-600 or
 * gray-400), muted and tertiary text as the chrome retunes them, and the
 * credits' Add link.
 */
export const CHROME_INKS: Record<ChromeMode, Record<ChromeInkName, Rgb>> = {
  light: {
    foreground: hexToRgb('#111111'),
    label: hexToRgb('#374151'),
    icon: hexToRgb('#4B5563'),
    muted: parseHslTriplet(CHROME_RETUNES.light['--muted-foreground']),
    tertiary: parseHslTriplet(CHROME_RETUNES.light['--tertiary-foreground']),
    link: hexToRgb('#1F4FD1'),
  },
  dark: {
    foreground: hexToRgb('#FFFFFF'),
    label: hexToRgb('#D1D5DB'),
    icon: hexToRgb('#9CA3AF'),
    muted: parseHslTriplet('220 9% 58%'),
    tertiary: parseHslTriplet(CHROME_RETUNES.dark['--tertiary-foreground']),
    link: parseHslTriplet('222 100% 65%'),
  },
};

/** A translucent fill the sidebar lays over the tint, as `bg-black/[0.05]`. */
export interface ChromeFill {
  color: Rgb;
  alpha: number;
}

/** Somewhere the sidebar draws text and icons, and which of its inks. */
export interface ChromeSurface {
  name: string;
  /** The fills over the tint and its grain, the lowest first; none for the bare tint. */
  fills: readonly ChromeFill[];
  inks: readonly ChromeInkName[];
}

const EVERY_INK: readonly ChromeInkName[] = [
  'foreground',
  'label',
  'icon',
  'muted',
  'tertiary',
  'link',
];

/**
 * Where the sidebar draws its inks in each mode (tabs-sidebar.styles.ts and
 * the controls that use it). Light fills that lighten the tint (tiles and
 * the command bar, white at 60% and 80%) only add contrast, so they are left
 * out; the account button's hover (4% black, 6% white) is no stronger than
 * SIDEBAR_HOVER.
 */
export const CHROME_SURFACES: Record<ChromeMode, readonly ChromeSurface[]> = {
  light: [
    { name: 'the tint', fills: [], inks: EVERY_INK },
    {
      name: 'a hovered row or button',
      fills: [{ color: BLACK, alpha: 0.05 }],
      inks: EVERY_INK,
    },
    {
      name: "an item's hovered actions button",
      fills: [{ color: BLACK, alpha: 0.07 }],
      inks: ['icon'],
    },
  ],
  dark: [
    { name: 'the tint', fills: [], inks: EVERY_INK },
    {
      name: 'a hovered row or button, a tile or the command bar',
      fills: [{ color: WHITE, alpha: 0.06 }],
      inks: EVERY_INK,
    },
    {
      name: 'a hovered tile or command bar',
      fills: [{ color: WHITE, alpha: 0.09 }],
      inks: ['foreground', 'label', 'icon', 'muted'],
    },
    {
      name: "a tile's hovered actions button",
      fills: [
        { color: WHITE, alpha: 0.06 },
        { color: WHITE, alpha: 0.1 },
      ],
      inks: ['icon'],
    },
  ],
};

export const CHROME_CARD: Record<ChromeMode, Rgb> = {
  light: hexToRgb('#FFFFFF'),
  dark: hexToRgb('#1E1E1E'),
};

export const CHROME_RING: Record<ChromeMode, Rgb> = {
  light: parseHslTriplet('222 100% 60%'),
  dark: parseHslTriplet('217 100% 62%'),
};

/** The grain pixel that costs the inks most: it pulls toward them. */
const WORST_GRAIN: Record<ChromeMode, Rgb> = { light: BLACK, dark: WHITE };

/** Each surface with its inks' luminance, worked out once. */
const SURFACE_INKS: Record<
  ChromeMode,
  readonly { fills: readonly ChromeFill[]; inkY: number[] }[]
> = {
  light: CHROME_SURFACES.light.map(({ fills, inks }) => ({
    fills,
    inkY: inks.map((ink) => luminance(CHROME_INKS.light[ink])),
  })),
  dark: CHROME_SURFACES.dark.map(({ fills, inks }) => ({
    fills,
    inkY: inks.map((ink) => luminance(CHROME_INKS.dark[ink])),
  })),
};
const CARD_Y: Record<ChromeMode, number> = {
  light: luminance(CHROME_CARD.light),
  dark: luminance(CHROME_CARD.dark),
};
const RING_Y: Record<ChromeMode, number> = {
  light: luminance(CHROME_RING.light),
  dark: luminance(CHROME_RING.dark),
};

/**
 * A surface as the screen blends it: the tint, a grain pixel over it at the
 * grain's opacity, then the fills.
 */
function chromeSurface(
  tint: Rgb,
  fills: readonly ChromeFill[],
  grainPixel: Rgb,
  grain: number
): Rgb {
  return fills.reduce(
    (surface, fill) => over(surface, fill.color, fill.alpha),
    over(tint, grainPixel, grainAlpha(grain))
  );
}

export interface ChromeContrast {
  /**
   * The weakest ink against the surface it is drawn on, each under the worst
   * grain pixel.
   */
  ink: number;
  /** The selected card against the tint's mean; below 1 when darker. */
  card: number;
  /** The focus ring against the tint under the worst grain pixel. */
  ring: number;
}

/** How the chrome reads on a tint at a grain. */
export function chromeContrast(
  tint: Rgb,
  mode: ChromeMode,
  grain: number
): ChromeContrast {
  const alpha = grainAlpha(grain);
  const worstY = luminance(over(tint, WORST_GRAIN[mode], alpha));
  const meanY = luminance(over(tint, MID_GREY, alpha));
  const cardY = CARD_Y[mode];
  let ink = Infinity;
  for (const { fills, inkY } of SURFACE_INKS[mode]) {
    const surfaceY = luminance(
      chromeSurface(tint, fills, WORST_GRAIN[mode], grain)
    );
    for (const y of inkY) ink = Math.min(ink, ratio(y, surfaceY));
  }
  return {
    ink,
    card:
      cardY > meanY
        ? ratio(cardY, meanY)
        : 1 / Math.max(1, ratio(cardY, meanY)),
    ring: ratio(RING_Y[mode], worstY),
  };
}

/** True when the chrome stays readable on this tint at this grain. */
export function passesChromeRules(
  tint: Rgb,
  mode: ChromeMode,
  grain: number
): boolean {
  const contrast = chromeContrast(tint, mode, grain);
  return (
    contrast.ink >= INK_CONTRAST &&
    contrast.card >= CARD_CONTRAST &&
    contrast.ring >= RING_CONTRAST
  );
}

/**
 * The darkest the dark chrome goes, in OKLab lightness: darker than this
 * leaves no room for a colour to show.
 */
export const DARK_FLOOR = 0.13;

const grey = (L: number): Rgb => oklabToRgb({ L, a: 0, b: 0 });

const bandCache = new Map<string, readonly [number, number]>();

/**
 * The OKLab lightness a tint may take in a mode at a grain, found on a
 * neutral grey: tone 0 maps to the first end, tone 100 to the second.
 */
export function chromeBand(
  mode: ChromeMode,
  grain: number
): readonly [number, number] {
  const level = Math.min(100, Math.max(0, Math.round(grain)));
  const key = `${mode}:${level}`;
  const cached = bandCache.get(key);
  if (cached) return cached;
  const passes = (L: number) => passesChromeRules(grey(L), mode, level);
  let first = -1;
  let last = -1;
  for (let step = 0; step <= 200; step++) {
    const L = step / 200;
    if (passes(L)) {
      if (first < 0) first = L;
      last = L;
    }
  }
  if (first < 0) throw new Error(`No legal ${mode} chrome at grain ${level}`);
  const edge = (inside: number, outside: number) => {
    for (let step = 0; step < 30; step++) {
      const L = (inside + outside) / 2;
      if (passes(L)) inside = L;
      else outside = L;
    }
    return inside;
  };
  const hi = last === 1 ? 1 : edge(last, last + 1 / 200);
  // The dark floor gives way should the rules ever set the top below it.
  const lo =
    mode === 'dark'
      ? Math.min(Math.max(DARK_FLOOR, first), hi)
      : first === 0
        ? 0
        : edge(first, first - 1 / 200);
  const band = [lo, Math.max(lo, hi)] as const;
  bandCache.set(key, band);
  return band;
}

/**
 * The 8-bit grey nearest lightness `L` that passes: at a heavy grain the dark
 * band can be narrower than one 8-bit step, and hold no grey of its own.
 */
function nearestGrey(L: number, passes: (rgb: Rgb) => boolean): Rgb {
  const level = Math.round(grey(L).r * 255);
  for (let step = 0; step <= 255; step++)
    for (const value of [level - step, level + step]) {
      if (value < 0 || value > 255) continue;
      const rgb = { r: value / 255, g: value / 255, b: value / 255 };
      if (passes(rgb)) return rgb;
    }
  throw new Error('No readable grey');
}

/**
 * The colour at lightness `L0` and hue `h`, moved toward the middle of the
 * band until the chrome reads on it. `chromaAt` gives the chroma at a
 * lightness. The result is an 8-bit colour, checked as the screen shows it.
 */
function clampToBand(
  L0: number,
  h: number,
  chromaAt: (L: number) => number,
  mode: ChromeMode,
  grain: number
): Rgb {
  const [lo, hi] = chromeBand(mode, grain);
  const at = (L: number) => quantize(oklchToRgb({ L, C: chromaAt(L), h }));
  const passes = (rgb: Rgb) => passesChromeRules(rgb, mode, grain);
  const start = at(L0);
  if (passes(start)) return start;
  const middle = (lo + hi) / 2;
  const centre = at(middle);
  if (!passes(centre)) {
    // Even the middle moves off with this much chroma: take chroma away.
    let inside = 0;
    let outside = chromaAt(middle);
    for (let step = 0; step < 24; step++) {
      const C = (inside + outside) / 2;
      if (passes(quantize(oklchToRgb({ L: middle, C, h })))) inside = C;
      else outside = C;
    }
    const muted = quantize(oklchToRgb({ L: middle, C: inside, h }));
    return passes(muted) ? muted : nearestGrey(middle, passes);
  }
  let bad = L0;
  let good = middle;
  let goodRgb = centre;
  for (let step = 0; step < 24; step++) {
    const L = (bad + good) / 2;
    const rgb = at(L);
    if (passes(rgb)) {
      good = L;
      goodRgb = rgb;
    } else bad = L;
  }
  return goodRgb;
}

/** An OKLCH colour clamped into the mode's band, keeping its chroma where it can. */
export function clampColour(lch: Oklch, mode: ChromeMode, grain: number): Rgb {
  return clampToBand(
    lch.L,
    lch.h,
    (L) => Math.min(lch.C, maxChroma(L, lch.h)),
    mode,
    grain
  );
}

/** The OKLab lightness a tone maps to in a mode at a grain. */
export function toneLightness(
  tone: number,
  mode: ChromeMode,
  grain: number
): number {
  const [lo, hi] = chromeBand(mode, grain);
  return lo + (Math.min(100, Math.max(0, tone)) / 100) * (hi - lo);
}

/**
 * A picked colour as the chrome shows it in a mode: the tone placed in the
 * mode's band, the chroma the intensity's share of the widest in gamut, then
 * clamped.
 */
export function resolveColour(
  color: AppearanceColor,
  intensity: number,
  mode: ChromeMode,
  grain: number
): Rgb {
  const share = Math.min(100, Math.max(0, intensity)) / 100;
  return clampToBand(
    toneLightness(color.tone, mode, grain),
    color.hue,
    (L) => share * maxChroma(L, color.hue),
    mode,
    grain
  );
}

const mixLab = (a: Oklab, b: Oklab, t: number): Oklab => ({
  L: a.L + (b.L - a.L) * t,
  a: a.a + (b.a - a.a) * t,
  b: a.b + (b.b - a.b) * t,
});

/** The OKLab mean of the chrome's colours, clamped: the tint under the gradient. */
export function meanTint(colors: Rgb[], mode: ChromeMode, grain: number): Rgb {
  if (colors.length === 1) return colors[0];
  const labs = colors.map(rgbToOklab);
  const sum = labs.reduce(
    (total, lab) => ({
      L: total.L + lab.L,
      a: total.a + lab.a,
      b: total.b + lab.b,
    }),
    { L: 0, a: 0, b: 0 }
  );
  const mean = {
    L: sum.L / labs.length,
    a: sum.a / labs.length,
    b: sum.b / labs.length,
  };
  return clampColour(oklabToOklch(mean), mode, grain);
}

/** The gradient runs mostly down the sidebar, leaning a little to the right. */
export const GRADIENT_ANGLE = 160;

/** Stops generated between each pair of colours. */
const STOPS_BETWEEN = 4;

/**
 * Every stop of the gradient across the colours, each clamped: the browser
 * would blend its own stops without the rules, so they are made here.
 */
export function gradientStops(
  colors: Rgb[],
  mode: ChromeMode,
  grain: number
): Rgb[] {
  if (colors.length < 2) return colors.slice();
  const labs = colors.map(rgbToOklab);
  const stops: Rgb[] = [];
  for (let index = 0; index < colors.length - 1; index++) {
    stops.push(colors[index]);
    for (let step = 1; step <= STOPS_BETWEEN; step++) {
      const lab = mixLab(
        labs[index],
        labs[index + 1],
        step / (STOPS_BETWEEN + 1)
      );
      stops.push(clampColour(oklabToOklch(lab), mode, grain));
    }
  }
  stops.push(colors[colors.length - 1]);
  return stops;
}

/** The chrome's gradient as CSS, or `none` for a single colour. */
export function buildGradient(
  colors: Rgb[],
  mode: ChromeMode,
  grain: number
): string {
  if (colors.length < 2) return 'none';
  const stops = gradientStops(colors, mode, grain);
  const last = stops.length - 1;
  const list = stops
    .map(
      (stop, index) =>
        `${rgbToHex(stop)} ${Math.round((index / last) * 10000) / 100}%`
    )
    .join(', ');
  return `linear-gradient(${GRADIENT_ANGLE}deg, ${list})`;
}

/** The grain's tile, in pixels. */
export const GRAIN_TILE = 160;

/**
 * The grain as a background layer: a tile of grey fractal noise at the
 * grain's opacity, as an inline SVG, or `none` at 0.
 */
export function buildGrain(grain: number): string {
  const alpha = grainAlpha(grain);
  if (alpha <= 0) return 'none';
  const opacity = Math.round(alpha * 10000) / 10000;
  const svg =
    `<svg xmlns='http://www.w3.org/2000/svg' width='${GRAIN_TILE}' height='${GRAIN_TILE}'>` +
    "<filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/>" +
    "<feColorMatrix type='saturate' values='0'/></filter>" +
    `<rect width='100%' height='100%' filter='url(#n)' opacity='${opacity}'/></svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}

/** Today's `--app-bg` in each mode, whose luminance a tinted one keeps. */
const STOCK_APP_BG: Record<ChromeMode, Rgb> = {
  light: parseHslTriplet('0 0% 94.9%'),
  dark: parseHslTriplet('30 8% 8%'),
};

/** The most chroma the page gutter takes from the tint. */
const APP_BG_MAX_CHROMA = 0.01;

/**
 * The page gutter behind the plain page card: today's luminance, at the
 * tint's hue with a quarter of its chroma, so the text on it reads as today.
 */
export function appBackground(tint: Rgb, mode: ChromeMode): Rgb {
  const target = luminance(STOCK_APP_BG[mode]);
  const { C, h } = rgbToOklch(tint);
  const chroma = Math.min(0.25 * C, APP_BG_MAX_CHROMA);
  const at = (L: number) =>
    oklchToRgb({ L, C: Math.min(chroma, maxChroma(L, h)), h });
  let lo = 0;
  let hi = 1;
  for (let step = 0; step < 30; step++) {
    const L = (lo + hi) / 2;
    if (luminance(at(L)) < target) lo = L;
    else hi = L;
  }
  return quantize(at((lo + hi) / 2));
}

/** The CSS variables the engine writes on `<html>`. */
export const APPEARANCE_TOKENS = [
  '--ap-tint-light',
  '--ap-tint-dark',
  '--ap-app-bg-light',
  '--ap-app-bg-dark',
  '--ap-gradient-light',
  '--ap-gradient-dark',
  '--ap-grain',
] as const;

export type AppearanceToken = (typeof APPEARANCE_TOKENS)[number];

export type AppearanceTokens = Record<AppearanceToken, string>;

/** The chrome's colours in one mode. */
export interface ChromeColours {
  /** Each picked colour, clamped. */
  picks: Rgb[];
  tint: Rgb;
  appBg: Rgb;
  gradient: string;
}

export function chromeColours(
  look: AppearanceLook,
  mode: ChromeMode
): ChromeColours | null {
  if (look.colors.length === 0) return null;
  const picks = look.colors.map((color) =>
    resolveColour(color, look.intensity, mode, look.grain)
  );
  const tint = meanTint(picks, mode, look.grain);
  return {
    picks,
    tint,
    appBg: appBackground(tint, mode),
    gradient: buildGradient(picks, mode, look.grain),
  };
}

/**
 * Every variable for a look, both modes at once so a mode switch needs no
 * script, or null with no colour picked (today's chrome).
 */
export function appearanceTokens(
  look: AppearanceLook
): AppearanceTokens | null {
  const light = chromeColours(look, 'light');
  const dark = chromeColours(look, 'dark');
  if (!light || !dark) return null;
  return {
    '--ap-tint-light': toHslTriplet(light.tint),
    '--ap-tint-dark': toHslTriplet(dark.tint),
    '--ap-app-bg-light': toHslTriplet(light.appBg),
    '--ap-app-bg-dark': toHslTriplet(dark.appBg),
    '--ap-gradient-light': light.gradient,
    '--ap-gradient-dark': dark.gradient,
    '--ap-grain': buildGrain(look.grain),
  };
}

/**
 * A colour as the picker draws it, the same in both modes so the hues stay
 * easy to tell apart: tone runs from a mid to a pale lightness, and the
 * intensity is the share of the widest chroma there.
 */
export function displayColour(
  hue: number,
  tone: number,
  intensity: number
): string {
  const L = 0.62 + (Math.min(100, Math.max(0, tone)) / 100) * 0.32;
  const C = (Math.min(100, Math.max(0, intensity)) / 100) * maxChroma(L, hue);
  return rgbToHex(oklchToRgb({ L, C, h: hue }));
}
